import type { Identified, TenantOwned } from "../shared/kernel/entity.js";
import type { UtcDateTime } from "../shared/kernel/date-time.js";
import type {
  DefinitionId,
  UserId
} from "../shared/kernel/identifiers.js";
import type { Branded } from "../shared/kernel/identifiers.js";
import type { JsonObject } from "../shared/kernel/json.js";

export type StudioVersionId = Branded<string, "StudioVersionId">;

export enum StudioVersionStatus {
  Draft = "DRAFT",
  Test = "TEST",
  Published = "PUBLISHED",
  Inactive = "INACTIVE",
  Archived = "ARCHIVED"
}

export interface StudioVersion
  extends Identified<StudioVersionId>, TenantOwned {
  readonly definitionId: DefinitionId;
  readonly versionNumber: number;
  readonly status: StudioVersionStatus;
  readonly configuration: JsonObject;
  readonly schemaHash: string;
  readonly publishedAt: UtcDateTime | null;
  readonly publishedBy: UserId | null;
  readonly createdBy: UserId;
  readonly updatedBy: UserId;
  readonly createdAt: UtcDateTime;
  readonly updatedAt: UtcDateTime;
  readonly version: number; // optimistic lock
}

/**
 * PUBLISHED versions are immutable.
 * This function enforces that invariant at the domain level.
 */
export function assertDraftOrTest(
  studioVersion: StudioVersion
): asserts studioVersion is StudioVersion & {
  readonly status: StudioVersionStatus.Draft | StudioVersionStatus.Test;
} {
  if (
    studioVersion.status !== StudioVersionStatus.Draft &&
    studioVersion.status !== StudioVersionStatus.Test
  ) {
    throw new TypeError(
      `Version ${studioVersion.versionNumber} is ${studioVersion.status} and cannot be modified. Create a new version instead.`
    );
  }
}

export function assertPublishedImmutable(
  studioVersion: StudioVersion
): void {
  if (studioVersion.status === StudioVersionStatus.Published) {
    throw new TypeError(
      "Published versions are immutable. Create a new version to make changes."
    );
  }
}
